// =============================================
// Admin Gate Devices Page - שערי נוכחות
// Kipikid - מערכת נוכחות ילדים בגנים
// ניהול שערי BLE (Gate / Shelter) לנוכחות אוטומטית באמצעות תג בתיק
// =============================================

import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { buildPairingPayload, generatePeerKey } from '../../utils/pairingQr';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio,
  Plus,
  X,
  Loader2,
  Copy,
  Check,
  KeyRound,
  Trash2,
  Settings2,
  Wrench,
  Power,
  AlertTriangle,
} from 'lucide-react';
import {
  adminApi,
  gateApi,
  getApiUrl,
  type AdminKindergartenDto,
  type KindergartenDeviceDto,
  type GateDeviceKeyResult,
  type GateDeviceType,
  type GateDeviceMode,
} from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStore } from '../../stores/adminStore';

const colors = {
  primary: '#6366f1',
  primaryLight: '#eef2ff',
  secondary: '#14b8a6',
  secondaryLight: '#ccfbf1',
  success: '#22c55e',
  successLight: '#dcfce7',
  successDark: '#166534',
  warning: '#f59e0b',
  warningLight: '#fef3c7',
  warningDark: '#92400e',
  error: '#dc2626',
  errorLight: '#fee2e2',
  text: '#1f2937',
  textLight: '#6b7280',
  border: '#e5e7eb',
  background: '#f9fafb',
};

// חיבור נחשב פעיל אם הייתה פעימת לב ב-5 הדקות האחרונות
const CONNECTED_WINDOW_MS = 5 * 60 * 1000;

const deviceTypeLabel: Record<GateDeviceType, string> = {
  Gate: 'שער',
  Shelter: 'מחסה',
  Tablet: 'טאבלט',
};

// חילוץ הודעת שגיאה בעברית מתשובת ה-API
const extractError = (err: unknown, fallback: string): string => {
  const e = err as { response?: { data?: { message?: string } } };
  return e?.response?.data?.message || fallback;
};

const formatHeartbeat = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

// סגירת מודל רק כשהלחיצה (mouse down) התחילה על הרקע עצמו
const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
  padding: '16px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px',
  fontSize: '14px',
  border: `2px solid ${colors.border}`,
  borderRadius: '10px',
  outline: 'none',
  boxSizing: 'border-box',
  direction: 'rtl',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 500,
  color: colors.text,
  marginBottom: '6px',
};

export default function AdminGateDevices() {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useAdminStore();
  const orgFilter = user?.organizationPublicId || selectedOrganizationId || undefined;

  const [devices, setDevices] = useState<KindergartenDeviceDto[]>([]);
  const [kindergartens, setKindergartens] = useState<AdminKindergartenDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [kgFilter, setKgFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const [showCreate, setShowCreate] = useState(false);
  const [editDevice, setEditDevice] = useState<KindergartenDeviceDto | null>(null);
  const [equipDevice, setEquipDevice] = useState<KindergartenDeviceDto | null>(null);
  const [deleteDevice, setDeleteDevice] = useState<KindergartenDeviceDto | null>(null);
  const [rotateDevice, setRotateDevice] = useState<KindergartenDeviceDto | null>(null);
  const [liveDevice, setLiveDevice] = useState<KindergartenDeviceDto | null>(null);
  const [keyResult, setKeyResult] = useState<GateDeviceKeyResult | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  // רשימת הגנים (לסינון וליצירה) - נטענת מחדש בהחלפת ארגון
  useEffect(() => {
    const loadKindergartens = async () => {
      try {
        const result = await adminApi.getKindergartens(orgFilter);
        if (result.success && result.data) {
          setKindergartens(result.data);
        }
      } catch (err) {
        console.error('Failed to load kindergartens:', err);
      }
    };
    setKgFilter('');
    loadKindergartens();
  }, [orgFilter]);

  // טעינת השערים - הפונקציה בתוך ה-effect (מניעת stale closure)
  useEffect(() => {
    const loadDevices = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const result = await gateApi.getDevices(kgFilter || undefined);
        if (result.success && result.data) {
          setDevices(result.data);
        } else {
          setLoadError(result.message || 'שגיאה בטעינת השערים');
        }
      } catch (err) {
        setLoadError(extractError(err, 'שגיאה בטעינת השערים'));
      } finally {
        setLoading(false);
      }
    };
    loadDevices();
  }, [kgFilter, refreshKey]);

  // כשיש ארגון נבחר מציגים רק שערים של גנים בארגון (הסינון בצד לקוח)
  const kgIds = new Set(kindergartens.map(k => k.publicId));
  const visibleDevices = orgFilter && kindergartens.length > 0
    ? devices.filter(d => kgIds.has(d.kindergartenPublicId))
    : devices;

  const refresh = () => setRefreshKey(k => k + 1);

  const runUpdate = async (device: KindergartenDeviceDto, data: Parameters<typeof gateApi.updateDevice>[1], fallback: string) => {
    setBusyId(device.publicId);
    setActionError('');
    try {
      const result = await gateApi.updateDevice(device.publicId, data);
      if (!result.success) {
        setActionError(result.message || fallback);
      }
      refresh();
    } catch (err) {
      setActionError(extractError(err, fallback));
    } finally {
      setBusyId(null);
    }
  };

  const handleModeClick = (device: KindergartenDeviceDto) => {
    if (device.mode === 'Shadow') {
      // מעבר ל-Live דורש אישור
      setLiveDevice(device);
    } else {
      runUpdate(device, { mode: 'Shadow' }, 'שגיאה במעבר למצב Shadow');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
            שערי נוכחות
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            {visibleDevices.length} שערים מוגדרים
          </p>
        </div>
        <motion.button
          onClick={() => setShowCreate(true)}
          whileTap={{ scale: 0.98 }}
          style={{
            padding: '12px 20px',
            borderRadius: '12px',
            border: 'none',
            backgroundColor: colors.primary,
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Plus size={18} />
          שער חדש
        </motion.button>
      </div>

      {/* Filter */}
      <select
        value={kgFilter}
        onChange={(e) => setKgFilter(e.target.value)}
        style={{ ...inputStyle, backgroundColor: 'white', maxWidth: '360px' }}
      >
        <option value="">כל הגנים</option>
        {kindergartens.map(k => (
          <option key={k.publicId} value={k.publicId}>{k.name}</option>
        ))}
      </select>

      {actionError && (
        <div style={{ padding: '12px', borderRadius: '10px', backgroundColor: colors.errorLight, color: colors.error, fontSize: '13px' }}>
          {actionError}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען נתונים...
        </div>
      ) : loadError ? (
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '16px', color: colors.error }}>
          {loadError}
        </div>
      ) : visibleDevices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '16px' }}>
          <p style={{ color: colors.textLight }}>לא הוגדרו שערים</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {visibleDevices.map((device, index) => (
            <DeviceCard
              key={device.publicId}
              device={device}
              index={index}
              busy={busyId === device.publicId}
              onEdit={() => setEditDevice(device)}
              onEquipment={() => setEquipDevice(device)}
              onToggleMode={() => handleModeClick(device)}
              onToggleActive={() => runUpdate(device, { isActive: !device.isActive }, 'שגיאה בעדכון סטטוס השער')}
              onRotate={() => setRotateDevice(device)}
              onDelete={() => setDeleteDevice(device)}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {showCreate && (
          <CreateDeviceModal
            kindergartens={kindergartens}
            onClose={() => setShowCreate(false)}
            onCreated={(result) => {
              setShowCreate(false);
              setKeyResult(result);
              refresh();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {equipDevice && (
          <EditEquipmentModal
            device={equipDevice}
            onClose={() => setEquipDevice(null)}
            onSaved={() => {
              setEquipDevice(null);
              refresh();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {editDevice && (
          <EditCalibrationModal
            device={editDevice}
            onClose={() => setEditDevice(null)}
            onSaved={() => {
              setEditDevice(null);
              refresh();
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {liveDevice && (
          <ConfirmModal
            title="מעבר למצב Live"
            message={`במצב Live קריאות התג של השער "${liveDevice.name}" יסמנו נוכחות בפועל. במצב Shadow הקריאות נרשמות בלבד ללא סימון נוכחות. האם לעבור למצב Live?`}
            confirmLabel="עבור ל-Live"
            confirmColor={colors.success}
            onClose={() => setLiveDevice(null)}
            onConfirm={async () => {
              const d = liveDevice;
              setLiveDevice(null);
              await runUpdate(d, { mode: 'Live' }, 'שגיאה במעבר למצב Live');
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {deleteDevice && (
          <ConfirmModal
            title="מחיקת שער"
            message={`האם למחוק את השער "${deleteDevice.name}"? הפעולה אינה הפיכה והשער יפסיק לדווח.`}
            confirmLabel="מחק"
            confirmColor={colors.error}
            onClose={() => setDeleteDevice(null)}
            onConfirm={async () => {
              const d = deleteDevice;
              setDeleteDevice(null);
              setBusyId(d.publicId);
              setActionError('');
              try {
                const result = await gateApi.deleteDevice(d.publicId);
                if (!result.success) setActionError(result.message || 'שגיאה במחיקת השער');
                refresh();
              } catch (err) {
                setActionError(extractError(err, 'שגיאה במחיקת השער'));
              } finally {
                setBusyId(null);
              }
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {rotateDevice && (
          <ConfirmModal
            title="החלפת מפתח"
            message={`החלפת המפתח של "${rotateDevice.name}" תבטל את המפתח הקיים. השער יפסיק לעבוד עד שיוגדר עם הכתובת החדשה. להמשיך?`}
            confirmLabel="החלף מפתח"
            confirmColor={colors.warning}
            onClose={() => setRotateDevice(null)}
            onConfirm={async () => {
              const d = rotateDevice;
              setRotateDevice(null);
              setBusyId(d.publicId);
              setActionError('');
              try {
                const result = await gateApi.rotateKey(d.publicId);
                if (result.success && result.data) {
                  setKeyResult(result.data);
                  refresh();
                } else {
                  setActionError(result.message || 'שגיאה בהחלפת המפתח');
                }
              } catch (err) {
                setActionError(extractError(err, 'שגיאה בהחלפת המפתח'));
              } finally {
                setBusyId(null);
              }
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {keyResult && <KeyResultModal result={keyResult} onClose={() => setKeyResult(null)} />}
      </AnimatePresence>
    </div>
  );
}

// ---------- כרטיס שער ----------

function DeviceCard({
  device, index, busy, onEdit, onEquipment, onToggleMode, onToggleActive, onRotate, onDelete,
}: {
  device: KindergartenDeviceDto;
  index: number;
  busy: boolean;
  onEdit: () => void;
  onEquipment: () => void;
  onToggleMode: () => void;
  onToggleActive: () => void;
  onRotate: () => void;
  onDelete: () => void;
}) {
  // חישוב סטטוס חיבור לפי פעימת הלב האחרונה
  const heartbeat = device.lastHeartbeatAt ? new Date(device.lastHeartbeatAt).getTime() : null;
  const neverConnected = heartbeat === null || isNaN(heartbeat);
  const connected = !neverConnected && Date.now() - (heartbeat as number) <= CONNECTED_WINDOW_MS;

  const connLabel = connected ? 'פעיל' : neverConnected ? 'מעולם לא התחבר' : 'לא מחובר';
  const connBg = connected ? colors.successLight : neverConnected ? colors.background : colors.errorLight;
  const connColor = connected ? colors.successDark : neverConnected ? colors.textLight : colors.error;

  const isLive = device.mode === 'Live';

  const smallBtn = (bg: string, color: string): React.CSSProperties => ({
    flex: 1,
    padding: '8px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: bg,
    color,
    fontSize: '12px',
    fontWeight: 500,
    cursor: busy ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    opacity: busy ? 0.6 : 1,
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: device.isActive ? 1 : 0.7, y: 0 }}
      transition={{ delay: index * 0.04 }}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
          backgroundColor: colors.primaryLight, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Radio size={22} color={colors.primary} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, color: colors.text }}>{device.name}</h3>
          <p style={{ fontSize: '13px', color: colors.textLight }}>{device.kindergartenName}</p>
        </div>
        <span style={{
          padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
          backgroundColor: colors.primaryLight, color: colors.primary,
        }}>
          {deviceTypeLabel[device.deviceType] || device.deviceType}
        </span>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{
          padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
          backgroundColor: isLive ? colors.successLight : colors.warningLight,
          color: isLive ? colors.successDark : colors.warningDark,
        }}>
          {isLive ? 'Live' : 'Shadow'}
        </span>
        <span style={{
          padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
          backgroundColor: connBg, color: connColor,
        }}>
          {connLabel}
        </span>
        {!device.isActive && (
          <span style={{ padding: '3px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, backgroundColor: colors.errorLight, color: colors.error }}>
            מושבת
          </span>
        )}
      </div>

      <div style={{ fontSize: '12px', color: colors.textLight, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {!neverConnected && <span>פעימה אחרונה: {formatHeartbeat(device.lastHeartbeatAt)}</span>}
        <span>
          סף RSSI: {device.rssiThreshold} | קריאות מינימום: {device.minReads} | חלון: {device.windowSeconds} שנ'
        </span>
        {device.gatewayMac && <span dir="ltr" style={{ textAlign: 'right' }}>MAC: {device.gatewayMac}</span>}
        {device.placementRole && <span>מיקום: {device.placementRole === 'entrance' ? 'כניסה' : 'תיקים'}</span>}
        {(device.model || device.serialNumber) && (
          <span dir="ltr" style={{ textAlign: 'right' }}>
            {[device.model, device.serialNumber].filter(Boolean).join(' | ')}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        <button disabled={busy} onClick={onEdit} style={smallBtn(colors.primaryLight, colors.primary)}>
          <Settings2 size={14} /> כיול
        </button>
        <button disabled={busy} onClick={onEquipment} style={smallBtn(colors.background, colors.text)}>
          <Wrench size={14} /> פרטי ציוד
        </button>
        <button disabled={busy} onClick={onToggleMode} style={smallBtn(isLive ? colors.warningLight : colors.successLight, isLive ? colors.warningDark : colors.successDark)}>
          {isLive ? 'חזור ל-Shadow' : 'עבור ל-Live'}
        </button>
        <button disabled={busy} onClick={onToggleActive} style={smallBtn(colors.background, colors.text)}>
          <Power size={14} /> {device.isActive ? 'השבת' : 'הפעל'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button disabled={busy} onClick={onRotate} style={smallBtn(colors.warningLight, colors.warningDark)}>
          <KeyRound size={14} /> החלף מפתח
        </button>
        <button disabled={busy} onClick={onDelete} style={smallBtn(colors.errorLight, colors.error)}>
          <Trash2 size={14} /> מחק
        </button>
      </div>
    </motion.div>
  );
}

// ---------- מודל יצירה ----------

function CreateDeviceModal({
  kindergartens, onClose, onCreated,
}: {
  kindergartens: AdminKindergartenDto[];
  onClose: () => void;
  onCreated: (result: GateDeviceKeyResult) => void;
}) {
  const [kindergartenId, setKindergartenId] = useState('');
  const [name, setName] = useState('');
  const [deviceType, setDeviceType] = useState<GateDeviceType>('Gate');
  const [gatewayMac, setGatewayMac] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canSave = kindergartenId && name.trim() && !saving;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await gateApi.createDevice({
        kindergartenPublicId: kindergartenId,
        name: name.trim(),
        deviceType,
        gatewayMac: gatewayMac.trim() || undefined,
      });
      if (result.success && result.data) {
        onCreated(result.data);
      } else {
        setError(result.message || 'שגיאה ביצירת השער');
      }
    } catch (err) {
      setError(extractError(err, 'שגיאה ביצירת השער'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title="שער חדש" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label style={labelStyle}>גן</label>
          <select value={kindergartenId} onChange={(e) => setKindergartenId(e.target.value)} style={{ ...inputStyle, backgroundColor: 'white' }}>
            <option value="">בחר גן...</option>
            {kindergartens.map(k => (
              <option key={k.publicId} value={k.publicId}>{k.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={labelStyle}>שם השער</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="למשל: שער כניסה ראשי" style={inputStyle} maxLength={100} />
        </div>
        <div>
          <label style={labelStyle}>סוג</label>
          <select value={deviceType} onChange={(e) => setDeviceType(e.target.value as GateDeviceType)} style={{ ...inputStyle, backgroundColor: 'white' }}>
            <option value="Gate">שער</option>
            <option value="Shelter">מחסה</option>
            <option value="Tablet">טאבלט (מפתח לטאבלט הגן)</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>כתובת MAC של השער (אופציונלי)</label>
          <input value={gatewayMac} onChange={(e) => setGatewayMac(e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" dir="ltr" style={{ ...inputStyle, direction: 'ltr' }} />
        </div>
        {error && <p style={{ fontSize: '13px', color: colors.error, textAlign: 'center' }}>{error}</p>}
        <ModalButtons
          onCancel={onClose}
          onConfirm={handleSave}
          disabled={!canSave}
          loading={saving}
          confirmLabel="צור שער"
          loadingLabel="יוצר..."
        />
      </div>
    </ModalShell>
  );
}

// ---------- מודל עריכת כיול ----------

function EditCalibrationModal({
  device, onClose, onSaved,
}: {
  device: KindergartenDeviceDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [rssi, setRssi] = useState(String(device.rssiThreshold));
  const [minReads, setMinReads] = useState(String(device.minReads));
  const [windowSeconds, setWindowSeconds] = useState(String(device.windowSeconds));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const rssiNum = Number(rssi);
    const minReadsNum = Number(minReads);
    const windowNum = Number(windowSeconds);
    if (!Number.isFinite(rssiNum) || !Number.isInteger(minReadsNum) || minReadsNum < 1 || !Number.isInteger(windowNum) || windowNum < 1) {
      setError('יש להזין ערכים מספריים תקינים');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const result = await gateApi.updateDevice(device.publicId, {
        rssiThreshold: rssiNum,
        minReads: minReadsNum,
        windowSeconds: windowNum,
      });
      if (result.success) {
        onSaved();
      } else {
        setError(result.message || 'שגיאה בשמירת הכיול');
      }
    } catch (err) {
      setError(extractError(err, 'שגיאה בשמירת הכיול'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={`כיול - ${device.name}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label style={labelStyle}>סף עוצמת אות (RSSI, למשל -70)</label>
          <input type="number" value={rssi} onChange={(e) => setRssi(e.target.value)} dir="ltr" style={{ ...inputStyle, direction: 'ltr' }} />
        </div>
        <div>
          <label style={labelStyle}>מספר קריאות מינימלי</label>
          <input type="number" min={1} value={minReads} onChange={(e) => setMinReads(e.target.value)} dir="ltr" style={{ ...inputStyle, direction: 'ltr' }} />
        </div>
        <div>
          <label style={labelStyle}>חלון זמן (שניות)</label>
          <input type="number" min={1} value={windowSeconds} onChange={(e) => setWindowSeconds(e.target.value)} dir="ltr" style={{ ...inputStyle, direction: 'ltr' }} />
        </div>
        {error && <p style={{ fontSize: '13px', color: colors.error, textAlign: 'center' }}>{error}</p>}
        <ModalButtons
          onCancel={onClose}
          onConfirm={handleSave}
          disabled={saving}
          loading={saving}
          confirmLabel="שמור"
          loadingLabel="שומר..."
        />
      </div>
    </ModalShell>
  );
}

// ---------- מודל פרטי ציוד ----------

const toDateInput = (value?: string | null) => (value ? value.slice(0, 10) : '');

function EditEquipmentModal({
  device, onClose, onSaved,
}: {
  device: KindergartenDeviceDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [serialNumber, setSerialNumber] = useState(device.serialNumber ?? '');
  const [model, setModel] = useState(device.model ?? '');
  const [firmwareVersion, setFirmwareVersion] = useState(device.firmwareVersion ?? '');
  const [purchasedAt, setPurchasedAt] = useState(toDateInput(device.purchasedAt));
  const [installedAt, setInstalledAt] = useState(toDateInput(device.installedAt));
  const [warrantyUntil, setWarrantyUntil] = useState(toDateInput(device.warrantyUntil));
  const [placementRole, setPlacementRole] = useState(device.placementRole ?? '');
  const [connectionType, setConnectionType] = useState(device.connectionType ?? '');
  const [notes, setNotes] = useState(device.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await gateApi.updateDevice(device.publicId, {
        serialNumber,
        model,
        firmwareVersion,
        placementRole,
        connectionType,
        notes,
        // תאריך ריק לא נשלח (אין ניקוי תאריך)
        ...(purchasedAt ? { purchasedAt } : {}),
        ...(installedAt ? { installedAt } : {}),
        ...(warrantyUntil ? { warrantyUntil } : {}),
      });
      if (result.success) {
        onSaved();
      } else {
        setError(result.message || 'שגיאה בשמירת פרטי הציוד');
      }
    } catch (err) {
      setError(extractError(err, 'שגיאה בשמירת פרטי הציוד'));
    } finally {
      setSaving(false);
    }
  };

  const ltr = { ...inputStyle, direction: 'ltr' as const };

  return (
    <ModalShell title={`פרטי ציוד - ${device.name}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div>
          <label style={labelStyle}>מספר סידורי</label>
          <input value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} dir="ltr" style={ltr} maxLength={64} />
        </div>
        <div>
          <label style={labelStyle}>דגם</label>
          <input value={model} onChange={(e) => setModel(e.target.value)} dir="ltr" style={ltr} maxLength={100} />
        </div>
        <div>
          <label style={labelStyle}>גרסת קושחה</label>
          <input value={firmwareVersion} onChange={(e) => setFirmwareVersion(e.target.value)} dir="ltr" style={ltr} maxLength={32} />
        </div>
        <div>
          <label style={labelStyle}>מיקום בגן</label>
          <select value={placementRole} onChange={(e) => setPlacementRole(e.target.value)} style={{ ...inputStyle, backgroundColor: 'white' }}>
            <option value="">לא הוגדר</option>
            <option value="entrance">כניסה</option>
            <option value="bags">אזור תיקים</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>אופן חיבור</label>
          <select value={connectionType} onChange={(e) => setConnectionType(e.target.value)} style={{ ...inputStyle, backgroundColor: 'white' }}>
            <option value="">לא הוגדר</option>
            <option value="wifi">Wi-Fi</option>
            <option value="ethernet">רשת קווית</option>
            <option value="ble">BLE בלבד</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>תאריך רכישה</label>
          <input type="date" value={purchasedAt} onChange={(e) => setPurchasedAt(e.target.value)} dir="ltr" style={ltr} />
        </div>
        <div>
          <label style={labelStyle}>תאריך התקנה</label>
          <input type="date" value={installedAt} onChange={(e) => setInstalledAt(e.target.value)} dir="ltr" style={ltr} />
        </div>
        <div>
          <label style={labelStyle}>אחריות עד</label>
          <input type="date" value={warrantyUntil} onChange={(e) => setWarrantyUntil(e.target.value)} dir="ltr" style={ltr} />
        </div>
        <div>
          <label style={labelStyle}>הערות</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={500} style={inputStyle} />
        </div>
        <p style={{ fontSize: '12px', color: colors.textLight }}>
          סיסמת ה-Wi-Fi אינה נשמרת כאן. פרטי הרשת של הגן מנוהלים בהגדרות הגן.
        </p>
        {error && <p style={{ fontSize: '13px', color: colors.error, textAlign: 'center' }}>{error}</p>}
        <ModalButtons
          onCancel={onClose}
          onConfirm={handleSave}
          disabled={saving}
          loading={saving}
          confirmLabel="שמור"
          loadingLabel="שומר..."
        />
      </div>
    </ModalShell>
  );
}

// ---------- מודל אישור כללי ----------

function ConfirmModal({
  title, message, confirmLabel, confirmColor, onClose, onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <ModalShell title={title} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <p style={{ fontSize: '14px', color: colors.text, lineHeight: 1.6 }}>{message}</p>
        <ModalButtons
          onCancel={onClose}
          onConfirm={() => { void onConfirm(); }}
          confirmLabel={confirmLabel}
          confirmColor={confirmColor}
        />
      </div>
    </ModalShell>
  );
}

// ---------- מודל הצגת מפתח (פעם אחת בלבד) ----------

function KeyResultModal({ result, onClose }: { result: GateDeviceKeyResult; onClose: () => void }) {
  const [copiedField, setCopiedField] = useState<'url' | 'key' | 'fallback' | null>(null);

  // כתובת מלאה: API base הקיים + נתיב השער (נתיב עשוי להגיע עם או בלי '/api' בתחילתו)
  const path = result.gatewayUrlPath.replace(/^\/+/, '');
  const fallbackUrl = /^https?:\/\//i.test(result.gatewayUrlPath)
    ? result.gatewayUrlPath
    : getApiUrl(path.replace(/^api\//, ''));
  // כתובת בלי המפתח (המפתח נשלח ב-header, ולכן לא נשמר בלוגים של השרת)
  const baseUrl = getApiUrl('gate/readings');

  const handleCopy = async (field: 'url' | 'key' | 'fallback', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      setCopiedField(null);
    }
  };

  const valueBox = (text: string) => (
    <div
      dir="ltr"
      style={{
        padding: '12px', borderRadius: '10px', backgroundColor: colors.background,
        border: `1px solid ${colors.border}`, fontSize: '12px', wordBreak: 'break-all',
        fontFamily: 'monospace', color: colors.text, textAlign: 'left',
      }}
    >
      {text}
    </div>
  );

  const copyButton = (field: 'url' | 'key' | 'fallback', value: string, label: string, primary = true) => (
    <motion.button
      onClick={() => { void handleCopy(field, value); }}
      whileTap={{ scale: 0.98 }}
      style={{
        padding: '10px', borderRadius: '10px',
        border: primary ? 'none' : `1px solid ${colors.border}`,
        backgroundColor: copiedField === field ? colors.success : (primary ? colors.primary : 'white'),
        color: copiedField === field || primary ? 'white' : colors.text,
        fontSize: '13px', fontWeight: 600, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
      }}
    >
      {copiedField === field ? <Check size={16} /> : <Copy size={16} />}
      {copiedField === field ? 'הועתק' : label}
    </motion.button>
  );

  return (
    <ModalShell title="מפתח השער נוצר" onClose={onClose} maxWidth="540px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <div style={{
          padding: '12px', borderRadius: '10px', backgroundColor: colors.warningLight, color: colors.warningDark,
          fontSize: '13px', lineHeight: 1.6, display: 'flex', gap: '8px', alignItems: 'flex-start',
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>
            המפתח מוצג פעם אחת בלבד ולא ניתן לשחזר אותו. יש להעתיק אותו עכשיו ולהגדיר את השער
            "{result.device.name}". אם אבד, יש להחליף מפתח.
          </span>
        </div>

        <div>
          <label style={labelStyle}>מומלץ: כתובת (POST) + מפתח ב-header</label>
          {valueBox(baseUrl)}
          <div style={{ marginTop: '8px' }}>{copyButton('url', baseUrl, 'העתק כתובת')}</div>
        </div>

        <div>
          <label style={labelStyle}>header: X-Gate-Key</label>
          {valueBox(result.deviceKey)}
          <div style={{ marginTop: '8px' }}>{copyButton('key', result.deviceKey, 'העתק מפתח')}</div>
        </div>

        <div>
          <label style={labelStyle}>חלופה לשער שאינו תומך ב-header: מפתח בכתובת</label>
          {valueBox(fallbackUrl)}
          <div style={{ marginTop: '8px' }}>{copyButton('fallback', fallbackUrl, 'העתק כתובת עם מפתח', false)}</div>
          <p style={{ fontSize: '12px', color: colors.textLight, marginTop: '6px', lineHeight: 1.5 }}>
            כתובת עם מפתח עלולה להישמר בלוגים של השרת. עדיף header כשהשער תומך בו (Minew G1, ESP32).
          </p>
        </div>

        <PairingQrSection deviceKey={result.deviceKey} valueBox={valueBox} />

        <button
          onClick={onClose}
          style={{
            padding: '12px', borderRadius: '10px', border: `1px solid ${colors.border}`,
            backgroundColor: 'white', color: colors.text, fontSize: '14px', cursor: 'pointer',
          }}
        >
          סיימתי, סגור
        </button>
      </div>
    </ModalShell>
  );
}

// ---------- קוד שיוך (QR) לטאבלט ----------

/**
 * יוצר QR עם מפתחות החיבור לטאבלט: מפתח המכשיר (מהחלון) ומפתח חתימה מול השערים.
 * מפתח החתימה לא נשמר בשרת: אפשר להדביק את הקיים (PEER_KEY בקושחה) או ליצור חדש, ואז להגדיר אותו גם בשער.
 * הקוד נשאר בזיכרון הדף בלבד ונעלם עם סגירת החלון.
 */
function PairingQrSection({ deviceKey, valueBox }: { deviceKey: string; valueBox: (text: string) => JSX.Element }) {
  const [open, setOpen] = useState(false);
  const [peerKey, setPeerKey] = useState('');
  const [includeDeviceKey, setIncludeDeviceKey] = useState(true);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleGenerate = async () => {
    setError('');
    setQrImage(null);
    try {
      const payload = buildPairingPayload({
        peerKey: peerKey || undefined,
        gateKey: includeDeviceKey ? deviceKey : undefined,
      });
      setQrImage(await QRCode.toDataURL(payload, { errorCorrectionLevel: 'M', margin: 2, width: 320 }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'יצירת הקוד נכשלה');
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          padding: '10px', borderRadius: '10px', border: `1px solid ${colors.primary}`,
          backgroundColor: 'white', color: colors.primary, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        קוד QR לשיוך הטאבלט
      </button>
    );
  }

  return (
    <div style={{ padding: '12px', borderRadius: '10px', border: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <label style={labelStyle}>מפתח חתימה מול השערים (אופציונלי, לא נשמר בשרת)</label>
      <input
        dir="ltr"
        value={peerKey}
        onChange={(e) => { setPeerKey(e.target.value); setQrImage(null); }}
        placeholder="הדבק את ה-PEER_KEY של השער, או צור חדש"
        autoComplete="off"
        spellCheck={false}
        style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px' }}
      />
      <button
        onClick={() => { setPeerKey(generatePeerKey()); setQrImage(null); }}
        style={{
          padding: '8px', borderRadius: '10px', border: `1px solid ${colors.border}`,
          backgroundColor: 'white', color: colors.text, fontSize: '13px', cursor: 'pointer',
        }}
      >
        צור מפתח חתימה חדש
      </button>
      {peerKey && valueBox(peerKey)}
      <p style={{ fontSize: '12px', color: colors.textLight, lineHeight: 1.5, margin: 0 }}>
        מפתח חדש צריך להיות מוגדר גם בקושחת כל שער בגן (PEER_KEY ב-config.h), אחרת השער יתעלם מהטאבלט. העתק אותו מכאן לפני סגירת החלון.
      </p>

      <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px' }}>
        <input type="checkbox" checked={includeDeviceKey} onChange={(e) => { setIncludeDeviceKey(e.target.checked); setQrImage(null); }} />
        כלול את מפתח המכשיר של שער זה (לטאבלט, להעלאת אירועים לשרת)
      </label>

      <button
        onClick={() => { void handleGenerate(); }}
        style={{
          padding: '10px', borderRadius: '10px', border: 'none',
          backgroundColor: colors.primary, color: 'white', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
        }}
      >
        הצג קוד QR
      </button>

      {error && <div style={{ color: colors.error, fontSize: '13px' }}>{error}</div>}

      {qrImage && (
        <div style={{ textAlign: 'center' }}>
          <img src={qrImage} alt="קוד שיוך לטאבלט" width={320} height={320} style={{ maxWidth: '100%' }} />
          <p style={{ fontSize: '12px', color: colors.warningDark, lineHeight: 1.5 }}>
            הקוד מכיל מפתחות סודיים. סרוק אותו מהטאבלט (הגדרות ⚙ ← סריקת קוד שיוך), אל תצלם ואל תשלח אותו, וסגור את החלון אחרי הסריקה.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------- רכיבי מודל משותפים ----------

function ModalShell({
  title, onClose, children, maxWidth = '420px',
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={backdropStyle}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white', borderRadius: '20px', padding: '24px',
          width: '100%', maxWidth, maxHeight: '90vh', overflowY: 'auto',
        }}
        dir="rtl"
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={20} color={colors.textLight} />
          </button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}

function ModalButtons({
  onCancel, onConfirm, confirmLabel, loadingLabel, disabled, loading, confirmColor = colors.primary,
}: {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel: string;
  loadingLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  confirmColor?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: '10px' }}>
      <motion.button
        onClick={onCancel}
        disabled={loading}
        whileTap={{ scale: 0.98 }}
        style={{
          flex: 1, padding: '12px', borderRadius: '10px', border: `1px solid ${colors.border}`,
          backgroundColor: 'white', color: colors.text, fontSize: '14px', fontWeight: 500, cursor: 'pointer',
        }}
      >
        ביטול
      </motion.button>
      <motion.button
        onClick={onConfirm}
        disabled={disabled}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        style={{
          flex: 1, padding: '12px', borderRadius: '10px', border: 'none',
          backgroundColor: confirmColor, opacity: disabled ? 0.5 : 1, color: 'white',
          fontSize: '14px', fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        }}
      >
        {loading && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
        {loading ? (loadingLabel || confirmLabel) : confirmLabel}
      </motion.button>
    </div>
  );
}
